import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Avatar,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import StorageIcon from '@mui/icons-material/Storage';
import api from '../../services/api';
import { getWelcomeMessage } from './customWelcomeMessage';
import { getAgentForTask } from '../../utils/agentNames';

/**
 * AI Agent Dialog Component
 * 
 * Dialog that displays when a user clicks on a task in the PRDflow Foundation page
 * Shows a centered avatar with a welcome message and interaction options
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {Object} props.task - The task that was clicked
 * @param {Object} props.user - Current user information
 * @param {string} props.projectId - Current project ID
 * @returns {React.ReactElement} Dialog component
 */
const AgentDialog = ({ 
  open, 
  onClose, 
  task = {},
  user = {},
  projectId
}) => {
  // Debug info
  console.log("AgentDialog props:", { open, task, user, projectId });
  console.log("Task contains section ID?", task?.sectionId || task?.section);
  console.log("Task ID candidates:", {
    id: task?.id,
    _id: task?._id,
    taskId: task?.taskId,
    task_id: task?.task_id,
    sectionId: task?.sectionId
  });
  console.log("Task title:", task?.title);

  // Direct mapping of task titles to agent names
  const getAgentByTaskTitle = (title) => {
    if (!title) return null;

    // Convert to lowercase for case-insensitive matching
    const normalizedTitle = title.toLowerCase().trim();

    // Direct mapping based on the requirements
    const agentMap = {
      'project info': { name: 'Proja', role: 'Project Info' },
      'features': { name: 'Featy', role: 'Features' },
      'user roles': { name: 'Rolie', role: 'User Roles' },
      'user stories': { name: 'Storia', role: 'User Stories' },
      'process flows': { name: 'Flo', role: 'Process Flows' },
      'knowledge hub analysis': { name: 'Felix', role: 'Analysis Orchestrator' },

      'ui/ux references': { name: 'Refa', role: 'UI/UX References' },
      'ui/ux reference': { name: 'Refa', role: 'UI/UX References' },
      'wireframes': { name: 'Wiro', role: 'UI/UX Wireframes' },
      'ui/ux wireframes': { name: 'Wiro', role: 'UI/UX Wireframes' },
      'design guidelines': { name: 'Guy', role: 'UI/UX Guidelines' },
      'ui/ux guidelines': { name: 'Guy', role: 'UI/UX Guidelines' },
      'design system': { name: 'Stylia', role: 'UI/UX Style Guide' },
      'ui/ux style guide': { name: 'Stylia', role: 'UI/UX Style Guide' },
      'designs': { name: 'Layo', role: 'UI/UX Layout' },
      'ui/ux layout': { name: 'Layo', role: 'UI/UX Layout' },
      'knowledge hub ui/ux': { name: 'Diana', role: 'UI/UX Orchestrator' },

      'field validations': { name: 'Fieldy', role: 'Field Validations' },
      'technology stack': { name: 'Stacky', role: 'Technology Stack' },
      'tech stack': { name: 'Stacky', role: 'Technology Stack' },
      'architecture': { name: 'Archie', role: 'Architecture' },
      'development guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'coding guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'dev guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'security guidelines': { name: 'Secury', role: 'Security Guidelines' },
      'user permissions': { name: 'Missy', role: 'User Permissions' },
      'knowledge hub coding': { name: 'Devon', role: 'Development Orchestrator' },
      'integration': { name: 'Integrity', role: 'Integration Specialist' },

      'functional testing': { name: 'Funcy', role: 'Functional Testing' },
      'automation testing': { name: 'Auton', role: 'Automation Testing' },
      'security testing': { name: 'Secu', role: 'Security Testing' },
      'performance testing': { name: 'Perfie', role: 'Performance Testing' },
      'knowledge hub quality': { name: 'Tessa', role: 'Quality Orchestrator' },

      'code version control': { name: 'Versio', role: 'Code Version Control' },
      'version control': { name: 'Versio', role: 'Code Version Control' },
      'ci/cd pipeline': { name: 'Liny', role: 'CI/CD Pipeline' },
      'deployment': { name: 'Deplo', role: 'Deployment' },
      'knowledge hub infrastructure': { name: 'Ian', role: 'Infrastructure Orchestrator' }
    };

    return agentMap[normalizedTitle] || null;
  };

  // First try direct mapping by task title
  const directAgent = task?.title ? getAgentByTaskTitle(task.title) : null;

  // If direct mapping fails, fall back to agent service
  const agentInfo = directAgent || getAgentForTask(task);
  console.log("Final agent info:", directAgent ? "From direct mapping" : "From agent service", agentInfo);

  // Get agent info for the task from our central agent system
  const taskAgent = agentInfo || { name: 'Ailyce', role: 'AI Agent' };
  
  // Use the section avatarUrl if available from the task's section
  const [avatarUrl, setAvatarUrl] = React.useState(null);

  // Fetch the avatar URL for the task agent when the dialog opens
  React.useEffect(() => {
    const fetchAvatarUrl = async () => {
      try {
        if (open && task) {
          // Get section ID from the task object
          const sectionId = task?.sectionId || (task?.section?._id || task?.section);

          if (!sectionId) {
            console.error("No section ID available for task");
            return;
          }

          // Use the API service to get avatar URL
          const response = await api.get(`/api/sections/${sectionId}/avatar`);
          const data = response.data;

          if (data.success && data.avatarUrl) {
            setAvatarUrl(data.avatarUrl);
            console.log("Set avatar URL to:", data.avatarUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching avatar URL:', error);
      }
    };

    fetchAvatarUrl();
  }, [task, open]);

  // Use the avatar URL if available
  const avatarImage = avatarUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'hidden',
          position: 'relative',
          top: '5vh', // Slightly offset from the top to center better in the main content area
          margin: '0 auto'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 2,
          px: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {task?.title || 'AI Assistant'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: 8,
            px: 4
          }}
        >
          {/* Large centered avatar */}
          <Avatar
            src={avatarImage}
            alt={taskAgent.name}
            sx={{
              width: 120,
              height: 120,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              mb: 3,
              bgcolor: 'transparent',  // Empty/transparent background
              border: '2px solid #ffcccc', // Light red border
              fontSize: '3rem',  // Larger text for the fallback avatar
              color: '#ff6666', // Light red text color
              '& > img': {
                objectFit: 'cover',
                width: '100%',
                height: '100%'
              }
            }}
          >
            {taskAgent.name.charAt(0).toUpperCase()}
          </Avatar>
          
          {/* Welcome message with dynamic content */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '450px' }}>
            {getWelcomeMessage(
              user?.firstName || user?.name || user?.username || 'there',
              taskAgent.name,
              task?.title || 'topic'
            ).split('\n\n').map((paragraph, index) => (
              <React.Fragment key={index}>
                {paragraph}
                {index < 2 && <><br/><br/></>}
              </React.Fragment>
            ))}
          </Typography>
          
          {/* Remove all the interaction options except one button */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 2
          }}>
            <Button 
              variant="contained" 
              startIcon={<ChatIcon />}
              sx={{ 
                textTransform: 'none', 
                minWidth: '160px',
                bgcolor: '#485869',
                '&:hover': {
                  bgcolor: '#3a4a59',
                }
              }}
              onClick={() => {
                // Navigate to the conversation with this task
                // Use same task ID resolution logic as Foundation component
                const taskId = task?.id || task?._id || task?.taskId || task?.task_id || task?.sectionId;
                console.log("AgentDialog Task object structure:", task);
                console.log("AgentDialog Available task IDs:", { 
                  id: task?.id, 
                  _id: task?._id, 
                  task_id: task?.task_id, 
                  taskId: task?.taskId,
                  sectionId: task?.sectionId,
                  computedTaskId: taskId
                });
                console.log("AgentDialog Navigating with task ID:", taskId);
                
                if (projectId && taskId) {
                  window.location.href = `/projects/${projectId}/conversation?task=${taskId}`;
                } else {
                  console.error("AgentDialog Cannot navigate: missing project ID or task ID", { 
                    projectId, 
                    taskId, 
                    task 
                  });
                }
              }}
            >
              Start Chat
            </Button>
          </Box>
        </Box>
      </DialogContent>
      
      {/* Removed DialogActions */}
    </Dialog>
  );
};

AgentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  task: PropTypes.object,
  user: PropTypes.object,
  projectId: PropTypes.string
};

export default AgentDialog;